import React, { Component } from "react";
import { AppCard, DataTableToolbar } from "../../components/ui";
import cx from "classnames";
import { AdditionalDataEnum, User } from "../../models/User";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import history from "../../history";
import { dateFilterTemplate, numberFilterMatchModeOptions, statusFilterMatchModeOptions, stringFilterMatchModeOptions } from "../Paginator";
import { dateBodyTemplate } from "../../utils/columnTemplates";
import { userApiService } from "../../services/api/users/UsersApiService";
import { IFilterModel, SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import { toastService } from "../../services/toastService";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { Link } from "react-router-dom";
import { TriStateCheckbox } from "primereact/tristatecheckbox";
import { SelectItem } from "../../models/SelectItem";
import { merchantApiService } from "../../services/api/merchants/MerchantApiService";
import { Dropdown } from "primereact/dropdown";
import { utils } from "../../utils/utils";
import { isRestrictedToLeadsView, isSuperAdmin } from "../../authGuard";

interface LeadsProps {
    merchantId: number | null;
    onClear: () => void;
    isDashboard: boolean;
}

interface LeadsState {
    leads: User[];
    merchants: SelectItem[];
    isLoading: boolean;
    isDashboard: boolean;
    totalRecords: number;
    selectedStatusFilter: string | null;
    fromSearchFilters: IFilterModel[]
}

export default class Leads extends Component<LeadsProps, LeadsState> {
    unlisten: (() => void) | null = null;
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: LeadsProps | Readonly<LeadsProps>) {
        super(props);

        this.state = {
            leads: [],
            merchants: [],
            isLoading: true,
            isDashboard: false,
            totalRecords: 0,
            selectedStatusFilter: null,
            fromSearchFilters: []
        };
    }

    async componentDidMount() {
        if (!isRestrictedToLeadsView() || isSuperAdmin()) {
            this.loadMerchants()
        }
        await this.handleSearchParams();

        this.unlisten = history.listen(async (location) => {
            if ((location.action == "PUSH" || location.action == "REPLACE") && location.location.pathname == "/users") {
                await this.handleSearchParams();
            }
        });
    }

    componentWillUnmount() {
        // Clean up the history listener
        if (this.unlisten) {
            this.unlisten();
        }
        this.setState({ fromSearchFilters: [] });
        this.dataTable?.reset()
    }

    handleSearchParams = async () => {
        const queryParams = new URLSearchParams(history.location.search);
        const totalUsers = queryParams.get("totalUsers");
        const userData = queryParams.get("userData");
        const searchInput = queryParams.get("searchInput");

        if (searchInput) {
            console.log("State Reset first when search from Global search ======")
            // Reset state first
            this.setState({
                leads: [],
                totalRecords: 0,
                fromSearchFilters: [],
            });
        }

        if (totalUsers && userData && searchInput) {
            const users = await JSON.parse(userData) as User[];
            const filters = await JSON.parse(searchInput) as IFilterModel[];

            // Filter only leads (users with id = 0)
            const leadUsers = users.filter(f => f.id === 0);

            // Set new state with fresh data
            this.setState({
                fromSearchFilters: filters,
                totalRecords: leadUsers.length,
                leads: leadUsers,
                isLoading: false,
            });
        }
    };

    loadMerchants = async () => {
        const merchantResult = await merchantApiService.getSelectItem();
        this.setState({ merchants: merchantResult.success ? merchantResult.data : [] });
    };

    loadLeadsData = async (searchRequest: SearchRequestBaseModel) => {
        // If we have search filters from URL or new filters from datatable
        if (history.location.search == "" || searchRequest.filters.length != 0 || this.state.fromSearchFilters.length > 0) {
            this.setState({ isLoading: true });

            // Use fromSearchFilters if they exist, otherwise use the provided filters
            searchRequest.filters = this.state.fromSearchFilters.length > 0
                ? this.state.fromSearchFilters
                : searchRequest.filters;

            const usersResult = await userApiService.onlyRegisteredUsers(searchRequest);

            if (!usersResult.success) {
                toastService.error(usersResult.message);
                this.setState({ isLoading: false });
                return;
            }

            this.setState({
                leads: usersResult.data ?? [],
                totalRecords: usersResult.totalCount ?? 0,
                isLoading: false
            });
        }
    };

    renderHeader = (hasActiveFilters: boolean) => <DataTableToolbar onClear={() => { this.props.onClear(); history.replace({ pathname: "/users" }); this.dataTable!.reset(); }} hasActiveFilters={hasActiveFilters} />;

    onRowClick = (event: any) => {
        const user = event.data as User;
        history.push(`/lead-profile/${user.userId}`);
    };

    SearchLeads = () => {
        console.log("searching...");
        var searchRequest = new SearchRequestBaseModel();

        this.loadLeadsData(searchRequest);
    };
    merchantFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <Dropdown
                value={options.value}
                options={this.state.merchants}
                onChange={(e) => options.filterCallback(e.value)}
                optionLabel="label"
                optionValue="value"
                filter
                showClear
                placeholder="Select Merchants"
                className="p-column-filter"
            />
        );
    };

    render() {
        const table = (
            <ExtendedDataTable
                header={this.renderHeader}
                value={this.state.leads}
                loading={this.state.isLoading}
                totalRecords={this.state.totalRecords}
                onChange={this.loadLeadsData}
                ref={(ref) => (this.dataTable = ref)}
                defaultSortOrder={SortOrderType.Descending}
                defaultSortField="createdTimeStamp"
                onRowClick={this.onRowClick}
            >
                <Column {...({ alwaysVisible: true } as any)} field="userId" header="Id" filterMatchModeOptions={numberFilterMatchModeOptions}></Column>
                <Column
                    field="firstName"
                    header="First Name"
                    {...({ alwaysVisible: true } as any)}
                    body={(rowData: User) => (
                        <Link to={`/lead-profile/${rowData.userId}`}>
                            {rowData.additionalData && rowData.additionalData.type == AdditionalDataEnum.FastpayAdditionalData ?
                                rowData.additionalData.data.firstName || "-" : "-"}
                        </Link>
                    )}
                    filterMatchModeOptions={stringFilterMatchModeOptions}
                ></Column>
                <Column
                    field="lastName"
                    header="Last Name"
                    {...({ alwaysVisible: true } as any)}
                    body={(rowData: User) => (
                        <Link to={`/lead-profile/${rowData.userId}`}>
                            {rowData.additionalData && rowData.additionalData.type == AdditionalDataEnum.FastpayAdditionalData ?
                                rowData.additionalData.data.lastName || "-" : "-"}
                        </Link>
                    )}
                    filterMatchModeOptions={stringFilterMatchModeOptions}
                ></Column>
                <Column field="email"
                    {...({ alwaysVisible: true } as any)}
                    header="Email" filterMatchModeOptions={stringFilterMatchModeOptions}
                    body={(rowData: User) => <Link to={`/lead-profile/${rowData.userId}`}>{rowData.email}</Link>}
                >
                </Column>
                <Column field="phoneNumber" header="Phone Number" filterMatchModeOptions={stringFilterMatchModeOptions}></Column>
                <Column
                    field="merchant"
                    filterMatchMode="equals"
                    header="Merchant"
                    filterField="MerchantId"
                    showFilterMatchModes={false}
                    filterMatchModeOptions={isSuperAdmin() ? stringFilterMatchModeOptions : null}
                    filterElement={this.merchantFilterTemplate}
                    headerStyle={{ minWidth: "120px" }}
                ></Column>
                <Column
                    field="merchantGroup"
                    header="Merchant Group"
                    filterMatchModeOptions={isSuperAdmin() ? stringFilterMatchModeOptions : null}
                    headerStyle={{ minWidth: "120px" }}
                ></Column>
                <Column
                    field="createdTimeStamp"
                    header="Created TimeStamp"
                    body={dateBodyTemplate("createdTimeStamp")}
                    filterMatchModeOptions={numberFilterMatchModeOptions}
                    dataType="date"
                    filterElement={dateFilterTemplate}
                ></Column>
                <Column
                    field="lastLogin"
                    header="Last Login"
                    body={dateBodyTemplate("lastLogin")}
                    filterMatchModeOptions={numberFilterMatchModeOptions}
                    dataType="date"
                    filterElement={dateFilterTemplate}
                ></Column>
                <Column
                    field="status"
                    header="Status"
                    body={"New"}
                ></Column>
            </ExtendedDataTable>
        );

        return table;
    }
}
